





































































import Vue from 'vue';
import { mapMutations, mapGetters } from 'vuex';

import { iconsMixin } from '@/mixins/icons';
import { breakpointsMixin } from '@/mixins/breakpoints';
import { formatMixin } from '@/mixins/format';

import { Account } from '@/models/Account';
import { Carrier } from '@/models/carriers/Carrier';
import { State } from '@/models/State';
import { ContractLookup } from '@/models/contracts/ContractLookup';
import RunSavedQueryRequest from '@/models/requests/RunSavedQueryRequest';
import { SavedQueryType } from '@/enums/SavedQueryType';
import { DataExtractByAccountRequest } from '@/models/requests/savedQueries/DataExtractByAccountRequest';
import { GetPreviousDaysShipmentsRequest } from '@/models/requests/savedQueries/GetPreviousDaysShipmentsRequest';
import { GetLeastCostExceptionsRequest } from '@/models/requests/savedQueries/GetLeastCostExceptionsRequest';
import { GetBookedShipmentsCountByUserRequest } from '@/models/requests/savedQueries/GetBookedShipmentsCountByUserRequest';
import { GetDailyItemizedSummaryWithPieceCountRequest } from '@/models/requests/savedQueries/GetDailyItemizedSummaryWithPieceCountRequest';
import { GetTenderRejectionDataExtractRequest } from '@/models/requests/savedQueries/GetTenderRejectionDataExtractRequest';
import { GetCarrierTotalsByYearMonthRequest } from '@/models/requests/savedQueries/GetCarrierTotalsByYearMonthRequest';
import { GetCustomerActivityRequest } from '@/models/requests/savedQueries/GetCustomerActivityRequest';
import { GetMonthlyTmsExcYtdRequest } from '@/models/requests/savedQueries/GetMonthlyTmsExcYtdRequest';
import { SpotMarketBidDataExtractRequest } from '@/models/requests/savedQueries/SpotMarketBidDataExtractRequest';
import { Last104WeeksOfDieselRequest } from '@/models/requests/savedQueries/Last104WeeksOfDieselRequest';
import { UpdateTlContractToFlatFeeRequest } from '@/models/requests/savedQueries/UpdateTlContractToFlatFeeRequest';
import { AddFedexFreightIotEconomyRequest } from '@/models/requests/savedQueries/AddFedexFreightIotEconomyRequest';
import { ServiceIncidentDataExtractRequest } from '@/models/requests/savedQueries/ServiceIncidentDataExtractRequest';

import accountsService from '@/services/AccountsService';
import carrierService from '@/services/CarrierService';
import savedQueryService from '@/services/SavedQueryService';
import mappingService from '@/services/MappingService';
import locationsService from '@/services/LocationsService';
import contractService from '@/services/ContractService';
import SendQueryResults from './SendQueryResults.vue';

export default Vue.extend<any, any, any, any>({
  components: { SendQueryResults },
  mixins: [iconsMixin, breakpointsMixin, formatMixin],
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
      loading: false,
      showEmailResultsDialog: false,
      loadingMessage: 'Loading Filters',
    };
  },
  created() {
    this.init();
  },
  methods: {
    ...mapMutations('savedQueries', [
      'setTypes',
      'setParameters',
      'setAccounts',
      'setCarriers',
      'setScacs',
      'setStates',
      'setContracts',
      'startRunningQuery',
      'stopRunningQuery',
      'setCurrentReportData',
      'setPage',
    ]),
    init() {
      this.loading = true;
      const typesCall = savedQueryService.getTypes();
      const paramsCall = savedQueryService.getParameters();
      const accountsCall = accountsService.getAllAccounts();
      const carriersCall = carrierService.getAllActive();
      const statesCall = locationsService.getListOfStates('USA');
      const contractsCall = contractService.getAll();

      Promise.all([
        typesCall,
        paramsCall,
        accountsCall,
        carriersCall,
        statesCall,
        contractsCall,
      ]).then((x) => {
        this.setTypes(x[0]);
        this.setParameters(x[1]);
        this.setAccounts((x[2] as Account[])?.map(mappingService.mapAccount));
        this.setCarriers((x[3] as Carrier[])?.map(mappingService.mapCarrier));
        this.setScacs(
          (x[3] as Carrier[])?.map(mappingService.mapCarrierWithScac)
        );
        this.setStates((x[4] as State[])?.map(mappingService.mapStateWithId));
        this.setContracts(
          (x[5] as ContractLookup[])?.map(mappingService.mapContractLookup)
        );

        this.loading = false;
      });
    },
    openEmailResultsDialog() {
      this.showEmailResultsDialog = true;
    },
    pageChange(newPage) {
      let page = this.currentReportData.slice(
        (newPage - 1) * this.pageSize,
        newPage * this.pageSize
      );

      this.setPage(page);
    },
    run() {
      const request = this.runSavedQueryRequest as RunSavedQueryRequest;

      this.startRunningQuery();
      const apiCall = this.getApiCall(request.queryName);
      if (!apiCall) {
        this.$toast.error(
          'Apologies, we could not find report executor for the selected query.'
        );
        this.stopRunningQuery();
        return;
      }

      apiCall
        .then((x) => {
          if (!x?.length) {
            this.$toast.error(
              'No results found for this query. Please try changing the filters.'
            );
            return;
          }

          if (
            request.queryName ==
              SavedQueryType.UPDATE_TL_CONTRACT_TO_FLAT_FEE ||
            request.queryName == SavedQueryType.ADD_FEDEX_FREIGHT_ECONOMY
          ) {
            this.$toast.show('The query has been executed successfully');
            return;
          }

          this.setCurrentReportData(x);

          this.setPage(x.slice(0, this.pageSize));
          this.currentPage = 1;
        })
        .finally(() => {
          this.stopRunningQuery();
        });
    },
    sendEmail(emails) {
      const request = this.runSavedQueryRequest as RunSavedQueryRequest;

      this.loadingMessage = 'Sending Email';
      this.loading = true;
      const apiCall = this.getApiCall(request.queryName, true, emails);
      apiCall
        .then((x) => {
          if (!x?.length) {
            this.$toast.error(
              'Email could not be sent. Please try again later.'
            );
          } else {
            this.$toast.show(
              'Email has been sent successfully, please check your inbox.'
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getApiCall(queryName: string, sendEmail = false, emails: string = null) {
      let data = null;
      const startDate = this.getStartDateParam?.value;
      const endDate = this.getEndDateParam?.value;
      const accountId = this.getAccountIdParam?.value;
      switch (queryName) {
        case SavedQueryType.DATA_EXTRACT_BY_ACCOUNT:
          data = new DataExtractByAccountRequest(
            sendEmail,
            emails,
            accountId,
            startDate,
            endDate
          );
          return savedQueryService.extractDataByAccount(data);
        case SavedQueryType.GET_PREVIOUS_DAYS_SHIPMENTS:
          data = new GetPreviousDaysShipmentsRequest(
            sendEmail,
            emails,
            startDate,
            endDate
          );
          return savedQueryService.getPreviousDaysShipments(data);
        case SavedQueryType.GET_LEAST_COST_EXCEPTIONS:
          data = new GetLeastCostExceptionsRequest(
            sendEmail,
            emails,
            accountId,
            startDate,
            endDate
          );
          return savedQueryService.getLeastCostExceptions(data);
        case SavedQueryType.GET_BOOKED_SHIPMENTS_COUNT_BY_USER:
          data = new GetBookedShipmentsCountByUserRequest(
            sendEmail,
            emails,
            accountId
          );
          return savedQueryService.getBookedShipmentsCountByUser(data);
        case SavedQueryType.DAILY_ITEMIZED_SUMMARY_WITH_PIECE_COUNT:
          data = new GetDailyItemizedSummaryWithPieceCountRequest(
            sendEmail,
            emails,
            accountId,
            startDate,
            endDate
          );
          return savedQueryService.getDailyItemizedSummaryWithPieceCount(data);
        case SavedQueryType.TENDER_REJECTION_DATA_EXTRACT:
          data = new GetTenderRejectionDataExtractRequest(
            sendEmail,
            emails,
            accountId,
            startDate,
            endDate
          );
          return savedQueryService.getTenderRejectionDataExtract(data);
        case SavedQueryType.GET_CARRIER_TOTALS_BY_YEAR_MONTH:
          data = new GetCarrierTotalsByYearMonthRequest(
            sendEmail,
            emails,
            startDate,
            endDate
          );
          return savedQueryService.getCarrierTotalsByYearMonth(data);
        case SavedQueryType.GET_CUSTOMER_ACTIVITY:
          data = new GetCustomerActivityRequest(
            sendEmail,
            emails,
            startDate,
            endDate
          );
          return savedQueryService.getCustomerActivity(data);
        case SavedQueryType.MONTHLY_TMS_EXC_YTD:
          data = new GetMonthlyTmsExcYtdRequest(
            sendEmail,
            emails,
            startDate,
            endDate
          );
          return savedQueryService.getMonthlyTmsExcYtd(data);
        case SavedQueryType.SPOT_MARKET_BID_DATA_EXTRACT:
          data = new SpotMarketBidDataExtractRequest(
            sendEmail,
            emails,
            accountId,
            startDate,
            endDate
          );
          return savedQueryService.spotMarketBidDataExtract(data);
        case SavedQueryType.LAST_104_WEEKS_OF_DIESEL:
          data = new Last104WeeksOfDieselRequest(sendEmail, emails);
          return savedQueryService.last104WeeksOfDiesel(data);
        case SavedQueryType.UPDATE_TL_CONTRACT_TO_FLAT_FEE:
          data = new UpdateTlContractToFlatFeeRequest(sendEmail, emails);
          return savedQueryService.updateTlContractToFlatFee(data);
        case SavedQueryType.ADD_FEDEX_FREIGHT_ECONOMY:
          data = new AddFedexFreightIotEconomyRequest(sendEmail, emails);
          return savedQueryService.addFedexFreightIotEconomy(data);
        case SavedQueryType.SERVICE_INCIDENT_DATA_EXTRACT:
          data = new ServiceIncidentDataExtractRequest(
            sendEmail,
            emails,
            accountId,
            startDate,
            endDate
          );
          return savedQueryService.serviceIncidentDataExtractReport(data);
      }
    },
  },
  computed: {
    ...mapGetters('savedQueries', [
      'runSavedQueryRequest',
      'currentReportData',
      'page',
    ]),
    pagingLength() {
      return Math.ceil(this.currentReportData.length / this.pageSize);
    },
    getAccountIdParam() {
      return this.runSavedQueryRequest?.params?.find((x) =>
        x.text?.toLowerCase().includes('account')
      );
    },
    getStartDateParam() {
      return this.runSavedQueryRequest?.params?.find(
        (x) =>
          x.text == 'StartDate' ||
          x.text == 'BeginDate' ||
          x.text == 'DateEnteredStart' ||
          x.text == 'DATE_ENTERED_START' ||
          x.text == 'BEGIN_DATE' ||
          x.text == 'begin_date' ||
          x.text == 'fromDate' ||
          x.text == 'EFF_DATE'
      );
    },
    getEndDateParam() {
      return this.runSavedQueryRequest?.params?.find(
        (x) =>
          x.text == 'EndDate' ||
          x.text == 'DateEnteredEnd' ||
          x.text == 'DATE_ENTERED_END' ||
          x.text == 'END_DATE' ||
          x.text == 'end_date' ||
          x.text == 'toDate'
      );
    },
  },
});
